import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Check } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ProductCard = ({ product, index = 0 }) => {
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const { addToCart } = useCart();
  const { isAdmin, isDriver } = useAuth();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isAdmin || isDriver) {
      toast.error('Admins and drivers cannot add items to cart');
      return;
    }

    if (!product.in_stock) {
      toast.error('This item is out of stock');
      return;
    }

    try {
      setAdding(true);
      await addToCart(product.id, 1, product);
      setAdded(true);
      toast.success(`${product.name} added to cart`);
      setTimeout(() => setAdded(false), 2000);
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Link 
      to={`/product/${product.id}`}
      className={`product-card bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden block opacity-0 animate-fade-in-up stagger-${(index % 6) + 1}`}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-stone-100">
        {product.image_url ? (
          <img 
            src={product.image_url} 
            alt={product.name}
            className="product-image w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/400x400/f5f5f4/78716c?text=${encodeURIComponent(product.name.split(' ')[0])}`; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-400 bg-stone-100">
            <span className="text-sm">{product.name.split(' ')[0]}</span>
          </div>
        )}
        
        {!product.in_stock && (
          <div className="absolute top-2 left-2 badge-error" data-testid="out-of-stock-badge">
            Out of Stock
          </div>
        )}
      </div>

      <div className="p-4">
        <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">{product.unit}</p>
        <h3 className="font-semibold text-stone-900 mb-2 line-clamp-2" data-testid="product-name">
          {product.name}
        </h3>
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-lg font-bold text-green-900" data-testid="product-price">
              R{product.selling_price.toFixed(2)}
            </span>
          </div>
          
          {!isAdmin && !isDriver && (
            <Button
              size="sm"
              className={`rounded-full w-10 h-10 p-0 transition-all ${
                added 
                  ? 'bg-green-600 hover:bg-green-600' 
                  : 'bg-green-900 hover:bg-green-800'
              }`}
              onClick={handleAddToCart}
              disabled={adding || !product.in_stock}
              data-testid={`add-to-cart-${product.id}`}
            >
              {added ? (
                <Check className="w-5 h-5" />
              ) : (
                <ShoppingCart className="w-5 h-5" />
              )}
            </Button>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
